import ConsentUtil from '@utils/ConsentUtil';
import PosthogUtil from '@utils/PosthogUtil';
import VTooltip from 'v-tooltip';
import Vue from 'vue';
import Skeleton from 'vue-loading-skeleton';
import SvgSprite from 'vue-svg-sprite';

let _crowdpower_loaded = false;

// MGR marketing tags only run on the primary brand domain, and never on
// end-customer surfaces (review / kiosk / showcase pages).
function isPrimaryDomain() {
    const domain = window.location.hostname.toLowerCase().replace('www.', '');
    return domain === process.env.VUE_APP_DOMAIN;
}

function isCustomerFacingPath(pathname = window.location.pathname) {
    const path = pathname.toLowerCase();
    return /^\/review(\/|$)/.test(path) ||
        /^\/kiosk(\/|$)/.test(path) ||
        /^\/showcase(\/|$)/.test(path);
}

function shouldLoadMgrMarketing(pathname) {
    return isPrimaryDomain() && !isCustomerFacingPath(pathname);
}

// Loads Help Scout Beacon after functional consent.
function loadHelpScout() {
    if (typeof window.Beacon !== 'undefined') return;

    window.Beacon = function(method, options, data) {
        window.Beacon.readyQueue.push({ method: method, options: options, data: data });
    };
    window.Beacon.readyQueue = [];

    function appendScript() {
        const existing = document.getElementsByTagName('script')[0];
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://beacon-v2.helpscout.net';
        existing.parentNode.insertBefore(script, existing);
    }

    if (document.readyState === 'complete') {
        appendScript();
    } else if (window.attachEvent) {
        window.attachEvent('onload', appendScript);
    } else {
        window.addEventListener('load', appendScript, false);
    }
}

// Loads Crowdpower after marketing consent (MGR marketing surfaces only).
function loadCrowdpower() {
    if (!shouldLoadMgrMarketing() || _crowdpower_loaded) return;

    _crowdpower_loaded = true;
    window.cp = window.cp || function() { (window.cp.q = window.cp.q || []).push(arguments); };
    window.cp('init', process.env.VUE_APP_CROWDPOWER_PROJECT_KEY);
    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.async = true;
    script.src = 'https://tag.crowdpower.io/js/app.js';
    document.body.appendChild(script);
}

// Enables or disables PostHog analytics capture from consent categories.
function syncPosthogAnalytics(categories, store) {
    if (categories.analytics) {
        PosthogUtil.enableAnalytics();
        if (store?.state?.user) {
            PosthogUtil.identify(store.state.user);
        }
    } else {
        PosthogUtil.disableAnalytics();
    }
}

// Hides Zaraz's built-in consent UI. Their hideConsentModal throws when the
// modal node was never mounted (common with a custom cookie banner).
function hideZarazConsentModal() {
    if (!window.zaraz?.consent) return;
    try {
        if (window.zaraz.consent.modal) {
            window.zaraz.consent.modal = false;
        }
    } catch (e) {
        // Ignore Zaraz DOM cleanup failures.
    }
}

// Pushes analytics and marketing consent into Zaraz.
// Marketing purposes stay off on white-label domains and customer-facing pages.
function syncZarazConsent(categories) {
    const analytics_id = process.env.VUE_APP_ZARAZ_ANALYTICS_PURPOSE_ID;
    const marketing_id = process.env.VUE_APP_ZARAZ_MARKETING_PURPOSE_ID;
    if (!window.zaraz?.consent) return;
    if (!analytics_id && !marketing_id) return;

    const purposes = {};
    if (analytics_id) purposes[analytics_id] = !!(categories && categories.analytics);
    if (marketing_id) {
        purposes[marketing_id] = shouldLoadMgrMarketing() && !!(categories && categories.marketing);
    }

    window.zaraz.consent.set(purposes);
    if (typeof window.zaraz.consent.sendQueuedEvents === 'function') {
        window.zaraz.consent.sendQueuedEvents();
    }
}

// Grants Zaraz purposes for opt-out regions / no consent banner.
// Marketing is only granted on MGR marketing surfaces.
function grantAllZarazConsent() {
    if (!window.zaraz?.consent) return;

    hideZarazConsentModal();

    if (!shouldLoadMgrMarketing()) {
        syncZarazConsent({ analytics: true, marketing: false });
        return;
    }

    if (typeof window.zaraz.consent.setAll === 'function') {
        window.zaraz.consent.setAll(true);
    }
    if (typeof window.zaraz.consent.sendQueuedEvents === 'function') {
        window.zaraz.consent.sendQueuedEvents();
    }
}

// Re-applies marketing gates after SPA navigations (e.g. leaving a review page).
function syncMarketingContext() {
    if (!ConsentUtil.isRequired()) {
        syncZarazConsent({ analytics: true, marketing: true });
        loadCrowdpower();
        return;
    }

    const consent = ConsentUtil.getConsent();
    const categories = consent
        ? consent.categories
        : { analytics: false, marketing: false };
    syncZarazConsent(categories);

    if (ConsentUtil.hasConsent('marketing')) {
        loadCrowdpower();
    }
}

// Waits for Zaraz Consent API, then grants all purposes.
function initZarazConsentAll() {
    function onReady() {
        grantAllZarazConsent();
    }

    if (window.zaraz?.consent?.APIReady) {
        onReady();
    } else {
        document.addEventListener('zarazConsentAPIReady', onReady);
    }
}

// Waits for Zaraz Consent API, hides its modal, and applies current consent.
function initZarazConsent() {
    if (!process.env.VUE_APP_ZARAZ_ANALYTICS_PURPOSE_ID && !process.env.VUE_APP_ZARAZ_MARKETING_PURPOSE_ID) return;

    function onReady() {
        if (!window.zaraz?.consent) return;

        // Consent gating stays on; only Zaraz's UI is disabled.
        hideZarazConsentModal();

        const consent = ConsentUtil.getConsent();
        syncZarazConsent(consent ? consent.categories : { analytics: false, marketing: false });
    }

    if (window.zaraz?.consent?.APIReady) {
        onReady();
    } else {
        document.addEventListener('zarazConsentAPIReady', onReady);
    }
}

// Boots consent handling and gates third-party scripts by category.
function initConsent(store) {
    PosthogUtil.init();

    // Banner not required (missing or 0) — opt-out model: load all non-essential scripts.
    if (!ConsentUtil.isRequired()) {
        initZarazConsentAll();
        loadHelpScout();
        loadCrowdpower();
        return;
    }

    ConsentUtil.init(store);
    initZarazConsent();

    if (ConsentUtil.hasConsent('analytics')) {
        PosthogUtil.enableAnalytics();
    }

    window.addEventListener('mgr:consent-updated', (e) => {
        syncPosthogAnalytics(e.detail, store);
        syncZarazConsent(e.detail);
    });

    ConsentUtil.onReady('functional', loadHelpScout);
    ConsentUtil.onReady('analytics', () => {
        PosthogUtil.enableAnalytics();
    });
    ConsentUtil.onReady('marketing', loadCrowdpower);
}

// Registers global Vue plugins used across the app.
function initPlugins() {
    // Skeleton loader.
    Vue.use(Skeleton);

    // SVG Sprite.
    Vue.use(SvgSprite, {
        url: require('./svg/sprites.svg'),
        class: 'icon',
    });

    // Tooltips.
    Vue.use(VTooltip, {
        defaultContainer: 'body',
        defaultDelay: { show: 100, hide: 100 },
        defaultPlacement: 'top',
    });
}

export const Plugins = {
    initPlugins: initPlugins,
    initConsent: initConsent,
    shouldLoadMgrMarketing: shouldLoadMgrMarketing,
    syncMarketingContext: syncMarketingContext,
};
